import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, mergeProps as _mergeProps, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "q-mb-md"
}
const _hoisted_2 = {
  key: 1,
  class: "q-mb-md"
}

import { computed, ref, watch } from "vue";
import { GenerateArtifactSchema } from "@/types";
import { ARTIFACT_GENERATION_OPTIONS } from "@/util";
import {
  appStore,
  artifactGenerationApiStore,
  artifactStore,
  selectionStore,
  timStore,
} from "@/hooks";
import {
  DetailsPanel,
  PanelCard,
  FlexBox,
  TextButton,
  ArtifactInput,
  ArtifactTypeInput,
  MultiselectInput,
} from "@/components/common";


/**
 * Displays inputs for generating new parent artifacts based on child artifacts.
 */
const __default__ = {
  name: "ArtifactGenerationPanel",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const mode = ref<"single" | "multiple">("single");
const childArtifactIds = ref<string[]>([]);
const childArtifactTypes = ref<string[]>([]);
const parentArtifactTypes = ref<string[]>([]);

const canGenerate = computed(() => {
  if (mode.value === "single") {
    return (
      childArtifactIds.value.length > 0 && parentArtifactTypes.value.length > 0
    );
  } else {
    return (
      childArtifactTypes.value.length > 0 &&
      parentArtifactTypes.value.length > 0
    );
  }
});

/**
 * Returns props for a mode button.
 * @param option - The mode button to get props for.
 */
function buttonProps(option: "single" | "multiple") {
  const selected = mode.value === option;

  return {
    text: !selected,
    color: selected ? undefined : "text",
    class: selected ? "button-group-selected text-primary" : "",
  };
}

/**
 * Clears all input fields.
 */
function handleReset(): void {
  if (selectionStore.selectedGroupIds.length > 0) {
    mode.value = "single";
    childArtifactIds.value = selectionStore.selectedGroupIds;
    childArtifactTypes.value = [];
  } else if (timStore.selectedArtifactLevel) {
    mode.value = "multiple";
    childArtifactTypes.value = [timStore.selectedArtifactLevel.name];
    childArtifactIds.value = [];
  } else {
    mode.value = "single";
    childArtifactIds.value = [];
    childArtifactTypes.value = [];
  }

  parentArtifactTypes.value = [];
}

/**
 * Generates new parent artifacts based on inputted child artifacts.
 */
function handleGenerate(): void {
  const config: GenerateArtifactSchema =
    mode.value === "single"
      ? {
          artifacts: childArtifactIds.value,
          targetTypes: parentArtifactTypes.value,
          clusters: [childArtifactIds.value],
        }
      : {
          artifacts: artifactStore.allArtifacts
            .filter(({ type }) => childArtifactTypes.value.includes(type))
            .map(({ id }) => id),
          targetTypes: parentArtifactTypes.value,
        };

  artifactGenerationApiStore.handleGenerateArtifacts(config, {
    onSuccess: () => {
      handleReset();
      appStore.closeSidePanels();
    },
  });
}
watch(
  () => appStore.popups.detailsPanel === "generateArtifact",
  (openState) => {
    if (!openState) return;

    handleReset();
  }
);

return (_ctx: any,_cache: any) => {
  const _component_q_btn_group = _resolveComponent("q-btn-group")!

  return (_openBlock(), _createBlock(_unref(DetailsPanel), { panel: "generateArtifact" }, {
    default: _withCtx(() => [
      _createVNode(_unref(PanelCard), {
        title: "Generate Artifacts",
        borderless: ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_btn_group, {
            flat: "",
            class: "q-mb-md"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(TextButton), _mergeProps(buttonProps('single'), {
                label: "Single",
                icon: "artifact",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (mode.value = 'single'))
              }), null, 16),
              _createVNode(_unref(TextButton), _mergeProps(buttonProps('multiple'), {
                label: "Multiple",
                icon: "nav-artifact",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (mode.value = 'multiple'))
              }), null, 16)
            ]),
            _: 1
          }),
          (mode.value === 'single')
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(ArtifactInput), {
                  modelValue: childArtifactIds.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((childArtifactIds).value = $event)),
                  multiple: "",
                  label: "Child Artifacts",
                  hint: "Generate a single parent artifact for these artifacts."
                }, null, 8, ["modelValue"])
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createVNode(_unref(ArtifactTypeInput), {
                  modelValue: childArtifactTypes.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((childArtifactTypes).value = $event)),
                  multiple: "",
                  label: "Child Artifact Types",
                  hint: "Generate multiple parent artifacts by clustering these types of artifacts by functionality."
                }, null, 8, ["modelValue"])
              ])),
          _createVNode(_unref(MultiselectInput), {
            modelValue: parentArtifactTypes.value,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((parentArtifactTypes).value = $event)),
            options: _unref(ARTIFACT_GENERATION_OPTIONS),
            label: "Parent Artifact Types",
            hint: "The type of parent artifacts to create. If multiple are selected, each type will be sequentially generated based on the past type."
          }, null, 8, ["modelValue", "options"]),
          _createVNode(_unref(FlexBox), {
            "full-width": "",
            justify: "end",
            t: "3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(TextButton), {
                disabled: !canGenerate.value,
                loading: _unref(artifactGenerationApiStore).artifactGenLoading,
                label: "Generate",
                color: "primary",
                onClick: handleGenerate
              }, null, 8, ["disabled", "loading"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})